// ─── Order Edit Modal (تعديل فاتورة قبل التأكيد) ───
// Used by the admin to add/remove items, change qty/price BEFORE confirming
// a pending order. The new total propagates to capital + stats automatically
// because the backend recomputes everything in /admin/orders/{id}/edit-items.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Search,
  Plus,
  Minus,
  Trash2,
  Loader2,
  Package,
  Check,
  Pencil,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const OrderEditModal = ({ order, onClose, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Hydrate items from the existing order on open.
  useEffect(() => {
    const initial = (order?.items || []).map((it) => ({
      product_id: it.product_id,
      name_ar: it.name_ar || it.name || "—",
      price: Number(it.price || 0),
      quantity: Number(it.quantity || 0),
      image_path: it.image_path || "",
    }));
    setItems(initial);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/products`);
        if (!cancelled) setProducts(data || []);
      } catch (err) {
        toast.error("فشل تحميل قائمة المنتجات");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [order]);

  const total = useMemo(
    () => items.reduce((s, it) => s + Number(it.price || 0) * Number(it.quantity || 0), 0),
    [items]
  );

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return products.slice(0, 30);
    return products
      .filter((p) => (p.name_ar || "").toLowerCase().includes(t) || (p.name || "").toLowerCase().includes(t))
      .slice(0, 30);
  }, [products, search]);

  const addItem = (p) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.product_id === p.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Number(next[idx].quantity || 0) + 1 };
        return next;
      }
      return [
        ...prev,
        {
          product_id: p.id,
          name_ar: p.name_ar || p.name,
          price: Number(p.price || 0),
          quantity: 1,
          image_path: p.image_path || "",
        },
      ];
    });
    setSearch("");
  };

  const setQty = (i, v) => {
    const q = Math.max(1, Number(v) || 1);
    setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, quantity: q } : x)));
  };
  const setPrice = (i, v) => {
    const p = Math.max(0, Number(v) || 0);
    setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, price: p } : x)));
  };
  const incQty = (i) => setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, quantity: Number(x.quantity || 0) + 1 } : x)));
  const decQty = (i) =>
    setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, quantity: Math.max(1, Number(x.quantity || 0) - 1) } : x)));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (items.length === 0) { toast.error("يجب وجود ماده واحدة على الأقل"); return; }
    setBusy(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          price: it.price,
        })),
      };
      const { data } = await axios.patch(`${API}/admin/orders/${order.id}/edit-items`, payload);
      // No more auto customer-balance handling — editing only touches stock.
      // Admin manages debt manually in الأرصدة.
      toast.success("تم تعديل الفاتورة وتحديث المخزون");
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التعديل");
    } finally {
      setBusy(false);
    }
  };

  if (!order) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-2" data-testid="order-edit-modal" dir="rtl">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[94vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-l from-[#F59E0B] to-[#F5A623] text-white p-4 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Pencil className="w-5 h-5" />
              <h3 className="font-black text-lg">تعديل الفاتورة</h3>
            </div>
            <p className="text-xs opacity-90">
              فاتورة #{order.invoice_number || order.id?.slice(0, 8)} · {order.user_name}
            </p>
            <p className="text-[10px] opacity-80 mt-0.5">
              يمكنك حذف ماده / إضافة ماده / تعديل الكميات والأسعار قبل التأكيد. عند الحفظ تتحدّث الأرباح ورأس المال تلقائياً.
            </p>
          </div>
          <button onClick={onClose} data-testid="order-edit-close" className="bg-white/20 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Current items */}
          <div>
            <h4 className="font-bold text-sm text-[#1E3A5F] mb-2 flex items-center gap-1">
              <Package className="w-4 h-4" /> المواد الحالية ({items.length})
            </h4>
            {items.length === 0 ? (
              <div className="bg-[#FEF2F2] rounded-xl p-4 text-center text-xs text-[#E85D45]">⚠️ لا توجد مواد. أضف على الأقل ماده واحدة.</div>
            ) : (
              <div className="space-y-1.5">
                {items.map((it, i) => (
                  <div key={it.product_id} data-testid={`order-edit-row-${i}`} className="bg-[#F8FAFC] rounded-xl p-2 flex items-center gap-2 border border-[#E4EBE6]">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate text-[#111814]">{it.name_ar}</p>
                      <div className="flex gap-1 items-center mt-1">
                        <input
                          type="number"
                          inputMode="decimal"
                          lang="en"
                          value={it.price}
                          onChange={(e) => setPrice(i, e.target.value)}
                          data-testid={`order-edit-price-${i}`}
                          className="w-24 px-1.5 py-0.5 text-[10px] font-bold rounded border border-[#3B82F6]/30 text-[#1E3A5F] bg-white"
                          dir="ltr"
                          title="سعر الوحدة"
                        />
                        <span className="text-[10px] text-[#5C6E64]">د.ع × {it.quantity} =</span>
                        <span className="text-xs font-black text-[#15803d]" dir="ltr">{fmt(it.price * it.quantity)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => decQty(i)} data-testid={`order-edit-dec-${i}`} className="w-7 h-7 bg-[#F3F6F4] hover:bg-[#E4EBE6] rounded-full flex items-center justify-center">
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        inputMode="numeric"
                        lang="en"
                        value={it.quantity}
                        onChange={(e) => setQty(i, e.target.value)}
                        data-testid={`order-edit-qty-${i}`}
                        className="w-12 text-center text-xs font-bold bg-white border border-[#3B82F6] rounded"
                        dir="ltr"
                      />
                      <button onClick={() => incQty(i)} data-testid={`order-edit-inc-${i}`} className="w-7 h-7 bg-[#7ED321] hover:bg-[#6cb91d] text-white rounded-full flex items-center justify-center">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeItem(i)}
                        data-testid={`order-edit-remove-${i}`}
                        title="حذف هذه الماده"
                        className="text-[#E85D45] hover:bg-[#FEE2E2] rounded p-1.5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add new item */}
          <div className="bg-[#F0FDF4] rounded-2xl p-3 border-2 border-[#7ED321]/40">
            <h4 className="font-bold text-sm text-[#15803d] mb-2 flex items-center gap-1">
              <Plus className="w-4 h-4" /> إضافة ماده جديدة
            </h4>
            <div className="relative mb-2">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6E64]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="order-edit-search"
                placeholder="ابحث عن المنتج..."
                className="w-full pr-8 pl-3 py-2 rounded-xl border border-[#7ED321]/40 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7ED321]"
              />
            </div>
            {loading ? (
              <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#15803d]" /></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto">
                {filtered.map((p) => {
                  const already = items.find((x) => x.product_id === p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addItem(p)}
                      data-testid={`order-edit-add-${p.id}`}
                      className="text-right bg-white hover:bg-[#F0FDF4] border-2 border-[#7ED321]/20 rounded-xl p-2 transition-colors relative"
                    >
                      {already && (
                        <span className="absolute top-1 right-1 bg-[#15803d] text-white rounded-full w-5 h-5 flex items-center justify-center text-[9px] font-black z-10" dir="ltr">
                          {already.quantity}
                        </span>
                      )}
                      <p className="text-[11px] font-bold text-[#111814] truncate">{p.name_ar}</p>
                      <p className="text-[9px] text-[#5C6E64]" dir="ltr">{fmt(p.price)} د.ع · مخزن {p.stock}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-[#E4EBE6] p-3 bg-[#F8FAFC]">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="font-bold text-[#1E3A5F]">الإجمالي الجديد:</span>
            <span className="font-black text-2xl text-[#15803d]" dir="ltr">{fmt(total)} <span className="text-sm opacity-70">د.ع</span></span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onClose}
              data-testid="order-edit-cancel"
              className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm"
            >
              إلغاء
            </button>
            <button
              onClick={save}
              disabled={busy || items.length === 0}
              data-testid="order-edit-save"
              className="bg-[#15803d] hover:bg-[#0e6b30] disabled:opacity-50 text-white font-bold py-2.5 rounded-full text-sm flex items-center justify-center gap-1.5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              حفظ التعديل
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderEditModal;
